"use client"

import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { ChevronDown } from "lucide-react"
import { AppLink } from "@/components/navigation/app-link"

const HERO_IMAGE = "/landing/ai_football.png"

export function HomeHero() {
  const [preferReducedMotion, setPreferReducedMotion] = useState(false)

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)")
    const update = () => setPreferReducedMotion(media.matches)
    update()
    media.addEventListener("change", update)
    return () => media.removeEventListener("change", update)
  }, [])

  return (
    <section className="relative flex min-h-svh items-center justify-center overflow-hidden">
      <img
        src={HERO_IMAGE}
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
        fetchPriority="high"
        decoding="async"
      />
      <div className="absolute inset-0 bg-black/40" />

      <div className="absolute inset-0">
        <motion.div
          className="absolute left-10 top-20 h-32 w-32 rounded-full bg-blue-400/20 blur-2xl"
          animate={{ y: [0, -20, 0], scale: [1, 1.1, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-20 right-20 h-48 w-48 rounded-full bg-purple-400/15 blur-3xl"
          animate={{ y: [0, 15, 0], scale: [1, 1.2, 1], opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
      </div>

      <div className="container relative z-10 mx-auto px-6 py-24">
        <div className="mx-auto max-w-4xl text-center">
          <motion.h1
            className="mb-6 text-5xl font-bold leading-tight text-white md:text-7xl lg:text-8xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            Own Your{" "}
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Game
            </span>
          </motion.h1>

          <motion.p
            className="mx-auto mb-10 max-w-3xl text-xl font-medium leading-snug text-white drop-shadow-md md:text-2xl lg:text-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            International football agency powered by data.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <AppLink
              href="/contact"
              className="group inline-flex rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-4 text-white transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/25"
            >
              <span className="flex items-center gap-2 font-medium">
                Get in touch
                <svg
                  className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </span>
            </AppLink>
          </motion.div>
        </div>
      </div>

      <motion.div
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 md:bottom-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.9 }}
      >
        <button
          type="button"
          onClick={() => {
            document.getElementById("mission")?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            })
          }}
          className="relative inline-flex flex-col items-center gap-1 px-5 py-3 text-white transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 before:pointer-events-none before:absolute before:inset-[-12px] before:-z-10 before:bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.55)_0%,transparent_70%)]"
        >
          <span className="text-xs font-semibold uppercase tracking-[0.22em] [text-shadow:0_1px_2px_rgba(0,0,0,0.8),0_0_16px_rgba(0,0,0,0.45)]">
            Scroll
          </span>
          <motion.span
            className="inline-flex drop-shadow-[0_1px_6px_rgba(0,0,0,0.85)]"
            aria-hidden
            animate={preferReducedMotion ? undefined : { y: [0, 4, 0] }}
            transition={
              preferReducedMotion
                ? undefined
                : { duration: 2.2, repeat: Infinity, ease: "easeInOut" }
            }
          >
            <ChevronDown className="h-7 w-7" strokeWidth={2.25} />
          </motion.span>
        </button>
      </motion.div>
    </section>
  )
}
